import { useState, useMemo, useEffect } from 'react';
import { PapiDeviation } from '@wix/da-papi-types';
import { isPastDate } from '@wix/da-i18next/pkg/time/functions';
import {
  getAllowOffers,
  getHasBuyItNow,
  getIsPending,
  getHasOwnOffer,
  getHasRejectedOffer,
  getIsOnSale,
  getIsAdoptableOfferType,
  getCanSeeMinPrice,
  getCanSeeOfferCountdown,
  getCanSeeEarlyAccessCountdown,
  getOfferCountdownEndTime,
  getEarlyAccessCountdownEndTime,
} from '../../../../utils/adoptables';
import { SellableAs } from '../../../../types/deviation';
import { getSellableAs } from '../../../../types/deviation/getters';

export default function useAdoptableState(deviation: PapiDeviation) {
  const offersList = deviation.extended?.adoptable?.offers?.offerList ?? [];
  const hasOffers = offersList.length > 0;
  const hasOwnOffer = getHasOwnOffer(deviation);
  const isOfferType = getIsAdoptableOfferType(deviation);
  const hasRejectedOffer = getHasRejectedOffer(deviation);
  const hasBuyItNow = getHasBuyItNow(deviation);
  const isOnSale = getIsOnSale(deviation);

  // this is kind of hacky because the deviation entity is not actually updated when timer ends
  // however, I don't think we want to force a page reload in this case, or manually alter entities (do we?)
  const offerCountdownEndTime = getOfferCountdownEndTime(deviation);
  const [offerCountdownHasEnded, setOfferCountdownHasEnded] = useState(
    offerCountdownEndTime ? isPastDate(offerCountdownEndTime) : false
  );
  const onOfferTimerEnd = () => setOfferCountdownHasEnded(true);

  // this is kind of hacky because the deviation entity is not actually updated when timer ends
  // however, I don't think we want to force a page reload in this case, or manually alter entities (do we?)
  const earlyAccessCountdownEndTime = getEarlyAccessCountdownEndTime(deviation);
  const [earlyAccessCountdownHasEnded, setEarlyAccessCountdownHasEnded] = useState(
    earlyAccessCountdownEndTime ? isPastDate(earlyAccessCountdownEndTime) : false
  );
  const onEarlyAccessTimerEnd = () => setEarlyAccessCountdownHasEnded(true);

  const isPending = useMemo(() => getIsPending(deviation), [deviation]);
  const allowOffers = useMemo(
    () => !isPending && getAllowOffers(deviation) && !offerCountdownHasEnded,
    [isPending, deviation, offerCountdownHasEnded]
  );

  const saleCount = deviation.extended?.adoptable?.saleCount ?? 0;
  const minOfferPrice = (allowOffers && deviation.adoptable?.offers?.offerPrice) || 0;
  const fixedPrice = (hasBuyItNow && deviation.adoptable?.fixedPrice?.fixedPrice) || 0;

  // Other flags
  const canSeeMinOfferPrice = getCanSeeMinPrice(deviation);
  const canSeeOfferCountdown = getCanSeeOfferCountdown(deviation);
  const canSeeEarlyAccessCountdown = getCanSeeEarlyAccessCountdown(deviation);
  const earlyAccess = deviation.extended?.adoptable?.earlyAccess ?? {
    isInEarlyAccess: false,
  };
  const hasEarlyAccessIfNeeded = earlyAccess.isSupporter || !earlyAccess.isInEarlyAccess;

  // Seller flags
  const isSeller = deviation.extended?.adoptable?.isOwner;
  const canSellerViewOffers =
    isSeller && ((isPending && hasOffers) || (isOnSale && (hasOffers || isOfferType)));
  const isResellOptionEnabled = getSellableAs(deviation).includes(SellableAs.AdoptableResale);

  // Buyer flags
  const adoptable = deviation.extended?.adoptable;
  const canAdopt = adoptable?.canAdopt;
  const isGiftable = adoptable?.isGiftable;
  const isFreeGiftable = deviation.extended?.adoptable?.isFreeGiftable;
  const freeGiftsRemaining = deviation.extended?.adoptable?.freeGiftsRemaining;
  const isBuyer = !isSeller;
  const canBuyNow = isBuyer && hasBuyItNow && canAdopt && hasEarlyAccessIfNeeded;
  const canMakeNewOffer =
    isBuyer && allowOffers && !hasOwnOffer && canAdopt && hasEarlyAccessIfNeeded;
  const canManageExistingOffer =
    isBuyer && allowOffers && hasOwnOffer && !hasRejectedOffer && hasEarlyAccessIfNeeded;
  const canBuyerViewOffers = isBuyer && isPending;

  useEffect(() => {
    setOfferCountdownHasEnded(offerCountdownEndTime ? isPastDate(offerCountdownEndTime) : false);
    // We update the value of `offerCountdownHasEnded` when `deviation` prop changes, to avoid having a stale state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviation]);

  useEffect(() => {
    setEarlyAccessCountdownHasEnded(
      earlyAccessCountdownEndTime ? isPastDate(earlyAccessCountdownEndTime) : false
    );
    // We update the value of `earlyAccessCountdownHasEnded` when `deviation` prop changes, to avoid having a stale state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviation]);

  return {
    isSeller,
    isGiftable,
    isFreeGiftable,
    freeGiftsRemaining,
    isOfferType,
    canAdopt,
    canBuyNow,
    canMakeNewOffer,
    canManageExistingOffer,
    canBuyerViewOffers,
    canSellerViewOffers,
    canSeeMinOfferPrice,
    canSeeEarlyAccessCountdown,
    canSeeOfferCountdown,
    hasOffers,
    offersList,
    saleCount,
    isResellOptionEnabled,
    minOfferPrice,
    fixedPrice,
    offerCountdownEndTime,
    offerCountdownHasEnded,
    onOfferTimerEnd,
    earlyAccessCountdownEndTime,
    earlyAccessCountdownHasEnded,
    onEarlyAccessTimerEnd,
    earlyAccess,
  };
}

export type UseAdoptableStateReturnType = ReturnType<typeof useAdoptableState>;
